/*
 * gg-header-price.js — Gummy Gardens, 2026-09-20
 *
 * Shows the real price under the product title: the list price struck through, the discounted total, and the
 * dollars saved, matching whichever purchase option is selected.
 *
 * Needed because the one-time multipack saving is delivered by Shopify AUTOMATIC discounts (5% on 2-packs, 10% on
 * 3-packs, one-time orders only). Automatic discounts apply in the cart and are invisible on a product page, so
 * without this the title price contradicts what the shopper is actually charged.
 *
 * SCOPE — deliberately narrow. This ONLY writes to the theme's own price element. It never writes anything inside
 * Recharge's subscription widget. An earlier version did, and it broke the widget: Recharge renders with lit, which
 * tracks its bindings with markers inside its nodes, so writing text or appending children corrupted them and
 * selecting a purchase option stopped working on exactly the packs that were touched. The widget is READ here (to
 * learn which option is selected) and never modified. Reading cannot break it.
 *
 * Rates must match Shopify's automatic discounts and the Recharge plans. If either changes, change them here.
 */
(function () {
  'use strict';

  var ONETIME = { '2PK': 0.05, '3PK': 0.10 };
  var SUB     = { '1PK': 0.10, '2PK': 0.15, '3PK': 0.20 };
  var TAG = 'gg-hdr-saving';
  var DETAIL = 'gg-onetime-detail';
  var BADGE = 'gg-onetime-badge';
  var SUBBADGE = 'gg-sub-badge';
  var SHIPBADGE = 'gg-onetime-ship';   // founder 2026-09-30: every order ships free — tag the one-time option too
  var ONEPRICE = 'gg-onetime-price';
  var GREEN = 'rgb(15,134,106)';   // Recharge's own badge green, so the savings read as one system

  function money(n) {
    var t = n.toFixed(2);
    return '$' + (t.slice(-3) === '.00' ? t.slice(0, -3) : t);
  }
  function round2(n) { return Math.round(n * 100) / 100; }

  function listPrice() {
    var el = document.querySelector('.price-item--regular');
    var m = ((el || {}).textContent || '').replace(/,/g, '').match(/\$\s*([\d.]+)/);
    return m ? parseFloat(m[1]) : null;
  }

  function pack() {
    var c = document.querySelector('.product-form__input input[type="radio"]:checked[value="1PK"], .product-form__input input[type="radio"]:checked[value="2PK"], .product-form__input input[type="radio"]:checked[value="3PK"]');
    return c ? c.value : null;
  }

  /** READ ONLY. Walks Recharge's open shadow roots to see which option is selected. Never writes. */
  function subscribeSelected() {
    var host = document.querySelector('recharge-subscription-widget');
    if (!host || !host.shadowRoot) return true;            // widget absent or not ready: assume the default
    var stack = [host.shadowRoot], guard = 0;
    while (stack.length && guard++ < 200) {
      var node = stack.shift(), els;
      try {
        var blk = node.querySelector('.rc-purchase-option__subscription');
        if (blk) {
          var radio = blk.querySelector('input[type="radio"]');
          if (radio) return !!radio.checked;
        }
        els = node.querySelectorAll('*');
      } catch (e) { continue; }
      for (var i = 0; i < els.length; i++) if (els[i].shadowRoot) stack.push(els[i].shadowRoot);
    }
    return true;
  }


  /* Founder 2026-09-30 (mobile): badges must move with their option and never float over the sticky header.
     They are mounted in the widget's light-DOM parent (outside Recharge's shadow DOM, so the widget itself is still
     never written to) and positioned relative to it, so they scroll with the page like part of the form. */
  function anchorBox() {
    var host = document.querySelector('recharge-subscription-widget');
    var a = host && host.parentElement;
    if (!a) return null;
    if (getComputedStyle(a).position === 'static') a.style.position = 'relative';
    return a;
  }
  function relLeft(x, a) { return Math.round(x - a.getBoundingClientRect().left); }
  function relTop(y, a) { return Math.round(y - a.getBoundingClientRect().top); }

  /** READ ONLY. Find an element inside Recharge's nested shadow roots. Never writes. */
  function findInWidget(selector) {
    var host = document.querySelector('recharge-subscription-widget');
    if (!host || !host.shadowRoot) return null;
    var stack = [host.shadowRoot], guard = 0;
    while (stack.length && guard++ < 200) {
      var node = stack.shift(), els;
      try {
        var hit = node.querySelector(selector);
        if (hit) return hit;
        els = node.querySelectorAll('*');
      } catch (e) { continue; }
      for (var i = 0; i < els.length; i++) if (els[i].shadowRoot) stack.push(els[i].shadowRoot);
    }
    return null;
  }

  /**
   * Float the saving badge over the one-time option, mirroring where Recharge puts its own.
   * It lives in the light DOM and is positioned from the option's measured rect, so the widget is never modified.
   * pointer-events is none: Recharge's own badge sits over its option and outside the <label>, which is why it
   * swallowed taps and made Subscribe unselectable. This one can never intercept a tap.
   */
  function paintBadge(id, selector, label) {
    var existing = document.getElementById(id);
    var box = label ? findInWidget(selector) : null;
    if (!box) { if (existing) existing.remove(); return; }
    // If Recharge already renders a badge on this option, sit exactly on top of it and match its width, so ours
    // covers theirs completely. Ours can be shorter than theirs ("Save $12 (15%)" vs "Save up to 15%"), so
    // matching the width matters — without it theirs peeks out from behind.
    var theirs = box.querySelector('.rc-purchase-option__badge');
    var r = (theirs ? theirs : box).getBoundingClientRect();
    if (!r.width) { if (existing) existing.remove(); return; }
    var coverWidth = theirs ? Math.ceil(r.width) : 0;
    var el = existing;
    if (!el) {
      el = document.createElement('div');
      el.id = id;
      (anchorBox() || document.body).appendChild(el);
    }
    el.textContent = label;
    // Founder 2026-09-30: dollars first ("Save $12 (15%)"), larger, and in the same flavour accent as the pack and
    // flavour buttons. --gg-accent / --gg-on-accent live on <html> (gg-flavor-theme.css) and change with the selected
    // flavour, so the badge recolours itself; brand purple when no flavour is set. Anchored on Recharge's badge
    // top-right corner (or -11px / 13px from the option's corner) and at least as wide, so theirs stays covered.
    el.setAttribute('style', [
      'position:absolute',
      'z-index:2',
      'pointer-events:none',
      'background:rgb(var(--gg-accent, 131, 122, 232))',
      'color:rgb(var(--gg-on-accent, 255, 255, 255))',
      'border-radius:6px',
      'padding:7px 10px',
      'font-family:Barlow,sans-serif',
      'font-size:14px',
      'font-weight:700',
      'line-height:14px',
      'letter-spacing:0.4px',
      'box-shadow:0 2px 6px rgba(0,26,48,.18)',
      'white-space:nowrap',
      coverWidth ? ('min-width:' + coverWidth + 'px') : '',
      'box-sizing:border-box',
      'text-align:center',
      'left:' + (anchorBox() ? relLeft(r.right - (theirs ? 0 : 13), anchorBox()) : Math.round(r.right + window.scrollX - (theirs ? 0 : 13))) + 'px',
      'top:' + (anchorBox() ? relTop(r.top - (theirs ? 4 : 13), anchorBox()) : Math.round(r.top + window.scrollY - (theirs ? 4 : 13))) + 'px',
      'transform:translateX(-100%)'
    ].join(';'));
  }


  /**
   * Cover Recharge's one-time price with the real one, so the option agrees with the price under the title.
   * Recharge prints the raw list price there ($120.00) because the one-time saving comes from a Shopify automatic
   * discount it knows nothing about. Painted over from the light DOM — the widget is still never written to.
   */
  function paintOneTimePrice(list, total) {
    var existing = document.getElementById(ONEPRICE);
    var box = (total != null) ? findInWidget('.rc-selection__onetime') : null;
    var theirPrice = box ? box.querySelector('.rc-price') : null;
    if (!theirPrice) { if (existing) existing.remove(); return; }

    var r = theirPrice.getBoundingClientRect();
    if (!r.width) { if (existing) existing.remove(); return; }

    var el = existing;
    if (!el) {
      el = document.createElement('div');
      el.id = ONEPRICE;
      (anchorBox() || document.body).appendChild(el);
    }
    el.innerHTML = '';
    var cs = getComputedStyle(theirPrice);
    // Match the surrounding surface so their figure is hidden rather than showing through.
    var bg = 'rgb(255,255,255)';
    var walk = theirPrice.parentElement, guard = 0;
    while (walk && guard++ < 6) {
      var c = getComputedStyle(walk).backgroundColor;
      if (c && c !== 'rgba(0, 0, 0, 0)' && c !== 'transparent') { bg = c; break; }
      walk = walk.parentElement;
    }
    var strike = document.createElement('span');
    strike.setAttribute('style', 'text-decoration:line-through;opacity:.6;margin-right:6px');
    strike.textContent = money(list);
    var now = document.createElement('span');
    now.textContent = money(total);
    el.appendChild(strike);
    el.appendChild(now);
    el.setAttribute('style', [
      'position:absolute',
      'z-index:1',
      'pointer-events:none',
      'background:' + bg,
      'color:' + cs.color,
      'font-family:' + cs.fontFamily,
      'font-size:' + cs.fontSize,
      'font-weight:' + cs.fontWeight,
      'line-height:' + cs.lineHeight,
      'white-space:nowrap',
      'left:' + (anchorBox() ? relLeft(r.left, anchorBox()) : Math.round(r.left + window.scrollX)) + 'px',
      'top:' + (anchorBox() ? relTop(r.top, anchorBox()) : Math.round(r.top + window.scrollY)) + 'px',
      'min-width:' + Math.ceil(r.width) + 'px',
      'min-height:' + Math.ceil(r.height) + 'px'
    ].join(';'));
  }


  /* "Free shipping" tag on the one-time option, same look as the savings tags. Sits just left of the savings tag
     (2-pack / 3-pack) or on its own where the savings tag would be (single bag). */
  function paintShipBadge() {
    paintBadge(SHIPBADGE, '.rc-selection__onetime', 'Free shipping');
    var ship = document.getElementById(SHIPBADGE);
    var money = document.getElementById(BADGE);
    if (!ship) return;
    ship.style.minWidth = '';
    if (money) {
      var m = money.getBoundingClientRect();
      var an = anchorBox();
      ship.style.left = (an ? relLeft(m.left - 6, an) : Math.round(m.left + window.scrollX - 6)) + 'px';
      ship.style.top = (an ? relTop(m.top, an) : Math.round(m.top + window.scrollY)) + 'px';
    }
  }

  function render() {
    var el = document.querySelector('.price-item--regular');
    if (!el) return;
    var holder = el.parentElement || el;

    var old = holder.querySelectorAll('.' + TAG);
    for (var i = 0; i < old.length; i++) old[i].remove();
    el.style.textDecoration = '';
    el.style.opacity = '';

    var list = listPrice();
    var p = pack();
    if (!list || !p) return;

    var oneRate = ONETIME[p] || 0;
    var rate = subscribeSelected() ? (SUB[p] || 0) : oneRate;

    if (rate) {
      var total = round2(list * (1 - rate));
      el.style.textDecoration = 'line-through';
      el.style.opacity = '0.6';
      var span = document.createElement('span');
      span.className = TAG;
      span.setAttribute('style', 'margin-left:8px;font-weight:700');
      var totalPart = document.createElement('span');
      totalPart.textContent = money(total);
      var savePart = document.createElement('span');
      savePart.setAttribute('style', 'color:' + GREEN + ';margin-left:6px');
      savePart.textContent = '(save ' + money(round2(list - total)) + ')';
      span.appendChild(totalPart);
      span.appendChild(savePart);
      holder.appendChild(span);
    }

    // The one-time multipack saving is the fact the page otherwise hides entirely, because an automatic discount
    // only appears in the cart. Stated here, under the price, rather than inside Recharge's widget — writing in
    // there is what broke option selection before.
    // Founder 2026-09-30: each option's tags show only while that option is selected — Subscribe shows its saving,
    // One-time shows Free shipping + its multipack saving — and the other option's tags are dropped.
    var subSel = subscribeSelected();
    var oneTotal = oneRate ? round2(list * (1 - oneRate)) : null;
    paintBadge(BADGE, '.rc-selection__onetime', (oneRate && !subSel) ? 'Save ' + money(round2(list - oneTotal)) + ' (' + Math.round(oneRate * 100) + '%)' : null);
    if (subSel) { var sb = document.getElementById(SHIPBADGE); if (sb) sb.remove(); } else { paintShipBadge(); }
    paintOneTimePrice(list, oneRate ? oneTotal : null);

    // Subscribe saving in dollars. Recharge's own badge sits underneath: covered by ours while Subscribe is selected,
    // hidden (style only — never content) while One-time is selected, so no stale tag shows on the unselected option.
    var subRate = SUB[p] || 0;
    paintBadge(SUBBADGE, '.rc-purchase-option__subscription', (subRate && subSel) ? 'Save ' + money(round2(list * subRate)) + ' (' + Math.round(subRate * 100) + '%)' : null);
    var theirs = findInWidget('.rc-purchase-option__subscription .rc-purchase-option__badge');
    if (theirs) theirs.style.visibility = subSel ? '' : 'hidden';
  }

  var pending = null;
  function schedule() { clearTimeout(pending); pending = setTimeout(render, 250); }

  // No scroll repositioning: badges live inside the form and scroll with it (re-measuring during iOS momentum scroll made them drift).
  window.addEventListener('resize', schedule);
  // Re-align when the widget changes size (an option expanding moves the boxes below it).
  (function watchWidget() {
    if (typeof ResizeObserver !== 'function') return;
    var host = document.querySelector('recharge-subscription-widget');
    if (!host) { setTimeout(watchWidget, 800); return; }
    new ResizeObserver(function () { schedule(); }).observe(host);
  })();

  function paintBadgeReposition() {
    var a = document.getElementById(BADGE);
    if (a) paintBadge(BADGE, '.rc-selection__onetime', a.textContent);
    if (document.getElementById(SHIPBADGE) && !subscribeSelected()) paintShipBadge();
    var b = document.getElementById(SUBBADGE);
    if (b) paintBadge(SUBBADGE, '.rc-purchase-option__subscription', b.textContent);
    schedule();   // the one-time price overlay is rebuilt from current values
  }

  document.addEventListener('change', schedule, true);
  document.addEventListener('click', schedule, true);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', schedule);
  else schedule();
  [600, 1500, 2600, 4000, 6000].forEach(function (ms) { setTimeout(schedule, ms); });
})();
